import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ShoppingCart, Search, Check, MapPin } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n || 0);
}

export default function ProcurementTab() {
    const [catalog, setCatalog] = useState([]);
    const [orders, setOrders] = useState([]);
    const [cart, setCart] = useState({}); // {product_id: qty}
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [delivery, setDelivery] = useState("");
    const [step, setStep] = useState("review"); // review | confirmed
    const [confirmation, setConfirmation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [fulfilmentMode, setFulfilmentMode] = useState("delivery"); // delivery | pickup
    const [pickupHubs, setPickupHubs] = useState([]);
    const [selectedHubId, setSelectedHubId] = useState(null);

    const load = async () => {
        const [c, o, w] = await Promise.all([
            api.get("/procurement/catalog"),
            api.get("/procurement/orders"),
            api.get("/wallet"),
        ]);
        setCatalog(c.data);
        setOrders(o.data);
        setWalletBalance(w.data.balance);
        // Load 3 closest hubs in the background
        api.get("/locations/nearby?radius_km=500")
            .then((r) => setPickupHubs((r.data.pickups || []).slice(0, 3)))
            .catch(() => setPickupHubs([]));
    };

    useEffect(() => { load(); }, []);

    const categories = useMemo(() => {
        const cats = Array.from(new Set(catalog.map((p) => p.category)));
        cats.sort();
        return ["All", ...cats];
    }, [catalog]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return catalog.filter((p) => {
            if (category !== "All" && p.category !== category) return false;
            if (!q) return true;
            return (
                p.name.toLowerCase().includes(q) ||
                (p.brand || "").toLowerCase().includes(q) ||
                p.supplier.toLowerCase().includes(q)
            );
        });
    }, [catalog, query, category]);

    const setQty = (id, delta) => {
        setCart((c) => {
            const next = { ...c };
            next[id] = Math.max(0, (next[id] || 0) + delta);
            if (next[id] === 0) delete next[id];
            return next;
        });
    };

    const setQtyExact = (id, qty) => {
        setCart((c) => {
            const next = { ...c };
            const v = Math.max(0, Math.floor(Number(qty) || 0));
            if (v === 0) delete next[id]; else next[id] = v;
            return next;
        });
    };

    const cartItems = useMemo(
        () => Object.entries(cart).map(([id, q]) => ({ ...(catalog.find((x) => x.id === id) || {}), qty: q })).filter((x) => x.id),
        [cart, catalog]
    );

    const total = cartItems.reduce((acc, it) => acc + it.price * it.qty, 0);
    const itemCount = cartItems.reduce((acc, it) => acc + it.qty, 0);
    const insufficient = total > walletBalance;

    const checkout = async () => {
        const items = cartItems.map((it) => ({ product_id: it.id, quantity: it.qty }));
        if (items.length === 0) {
            toast.error("Your cart is empty");
            return;
        }
        if (fulfilmentMode === "pickup" && !selectedHubId) {
            toast.error("Pick a collection hub or switch to delivery");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/procurement/orders", {
                items,
                delivery_address: delivery || undefined,
                fulfilment_mode: fulfilmentMode,
                pickup_host_id: fulfilmentMode === "pickup" ? selectedHubId : undefined,
            });
            setConfirmation(data.order);
            setWalletBalance(data.balance);
            setCart({});
            setStep("confirmed");
            toast.success(`Order placed • ${items.length} items`);
            api.get("/procurement/orders").then((r) => setOrders(r.data));
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const openCheckout = () => {
        if (cartItems.length === 0) {
            toast.error("Your cart is empty");
            return;
        }
        setStep("review");
        setConfirmation(null);
        // Default to nearest hub pre-selected (but still in delivery mode by default)
        if (!selectedHubId && pickupHubs[0]) setSelectedHubId(pickupHubs[0].id);
        setCheckoutOpen(true);
    };

    return (
        <div className="space-y-6" data-testid="procurement-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-3xl font-extrabold text-onego-navy">Procurement marketplace</h2>
                    <p className="text-slate-600">FMCG, beverages, household goods, milk, sugar, salt, cooking oil and more &mdash; ordered from suppliers.</p>
                </div>
                <button onClick={openCheckout} disabled={itemCount === 0} className="og-btn-gradient inline-flex items-center gap-2 disabled:opacity-50" data-testid="open-checkout-btn">
                    <ShoppingCart size={18} /> Checkout {itemCount > 0 ? `(${itemCount})` : ""}
                </button>
            </div>

            {/* Search + Category filter */}
            <div className="og-card p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        className="og-input pl-10"
                        placeholder="Search milk, sugar, brand, supplier\u2026"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="catalog-search"
                    />
                </div>
                <div className="flex flex-wrap gap-2">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCategory(c)}
                            className={`px-3 py-2 rounded-full text-xs font-bold border transition ${category === c ? "bg-onego-navy text-white border-onego-navy" : "border-slate-200 text-slate-600 hover:border-onego-navy"}`}
                            data-testid={`catalog-cat-${c.replace(/\s|&/g, "-")}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="catalog-grid">
                    {filtered.length === 0 && (
                        <p className="col-span-full text-sm text-slate-500 py-10 text-center" data-testid="catalog-empty">
                            No products match your filters.
                        </p>
                    )}
                    {filtered.map((p) => {
                        const qty = cart[p.id] || 0;
                        const oos = !p.in_stock || (p.stock || 0) <= 0;
                        // Determine the active tier for the current qty (or qty 1)
                        let activeTier = null;
                        for (const t of (p.tiers || [])) {
                            if ((qty || 1) >= t.min_qty) activeTier = t;
                        }
                        const displayPrice = activeTier ? activeTier.price : p.price;
                        const ruralPrice = p.region_pricing?.rural;
                        return (
                            <div key={p.id} className={`og-card p-5 flex flex-col ${oos ? "opacity-60" : ""}`} data-testid={`product-card-${p.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="og-chip">{p.category}</span>
                                    {oos ? (
                                        <span className="og-chip bg-rose-100 text-rose-700" data-testid={`product-oos-${p.id}`}>Out of stock</span>
                                    ) : (
                                        <span className="og-chip bg-emerald-50 text-emerald-700" data-testid={`product-stock-${p.id}`}>{p.stock} in stock</span>
                                    )}
                                </div>
                                <h3 className="mt-3 font-bold text-onego-navy leading-tight">{p.name}</h3>
                                <p className="text-xs text-slate-500 mt-0.5">{p.brand || p.supplier} &middot; per {p.unit}</p>
                                <div className="mt-3 flex items-baseline gap-2">
                                    <p className="font-display text-xl font-extrabold text-onego-navy">KES {fmt(displayPrice)}</p>
                                    {activeTier && activeTier.price !== p.price && (
                                        <span className="text-xs text-slate-400 line-through">KES {fmt(p.price)}</span>
                                    )}
                                </div>
                                {ruralPrice && ruralPrice !== p.price && (
                                    <p className="text-[11px] text-slate-500 mt-0.5">Rural: KES {fmt(ruralPrice)}</p>
                                )}
                                {p.tiers && p.tiers.length > 1 && (
                                    <div className="mt-3 flex flex-wrap gap-1" data-testid={`product-tiers-${p.id}`}>
                                        {p.tiers.map((t) => (
                                            <span
                                                key={t.min_qty}
                                                className={`text-[10px] px-2 py-1 rounded-full font-bold border ${activeTier?.min_qty === t.min_qty ? "border-pink-500 bg-pink-50 text-pink-700" : "border-slate-200 text-slate-500"}`}
                                                title={`${t.min_qty}+ units @ KES ${fmt(t.price)}`}
                                            >
                                                {t.min_qty}+ {t.label.includes("-") ? t.label.split(" ")[1] : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-auto pt-4 flex items-center gap-2">
                                    <button onClick={() => setQty(p.id, -1)} disabled={qty === 0 || oos} className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40" data-testid={`product-dec-${p.id}`}>
                                        <Minus size={16} />
                                    </button>
                                    <input
                                        type="number"
                                        min={0}
                                        max={p.stock}
                                        value={qty}
                                        onChange={(e) => setQtyExact(p.id, e.target.value)}
                                        disabled={oos}
                                        className="og-input text-center px-0 py-1 w-14"
                                        data-testid={`product-qty-${p.id}`}
                                    />
                                    <button onClick={() => setQty(p.id, +1)} disabled={oos || qty >= (p.stock || 0)} className="h-9 w-9 rounded-full og-gradient text-white flex items-center justify-center disabled:opacity-40" data-testid={`product-inc-${p.id}`}>
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <aside className="og-card p-6 h-fit sticky top-24" data-testid="cart-panel">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ShoppingCart size={18} className="text-onego-navy" />
                            <h3 className="font-display font-bold text-onego-navy">Your cart</h3>
                        </div>
                        <span className="og-chip">{itemCount}</span>
                    </div>
                    {cartItems.length === 0 && <p className="mt-3 text-sm text-slate-500">No items added yet.</p>}
                    <div className="mt-3 space-y-2 max-h-[260px] overflow-auto">
                        {cartItems.map((it) => (
                            <div key={it.id} className="flex items-start justify-between text-sm gap-3" data-testid={`cart-item-${it.id}`}>
                                <div className="min-w-0">
                                    <p className="font-semibold text-onego-navy truncate">{it.name}</p>
                                    <p className="text-xs text-slate-500">{it.qty} &times; KES {fmt(it.price)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-onego-navy">KES {fmt(it.qty * it.price)}</p>
                                    <button onClick={() => setQty(it.id, -it.qty)} className="text-rose-500 hover:underline text-xs inline-flex items-center gap-1 mt-1">
                                        <Trash2 size={12} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="og-divider-gradient my-4" />
                    <div className="flex items-center justify-between text-sm">
                        <p className="text-slate-500">Wallet balance</p>
                        <p className="font-semibold text-onego-navy">KES {fmt(walletBalance)}</p>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                        <p className="text-sm text-slate-500">Total</p>
                        <p className="font-display text-xl font-extrabold text-onego-navy" data-testid="cart-total">KES {fmt(total)}</p>
                    </div>
                    {insufficient && cartItems.length > 0 && (
                        <p className="mt-2 text-xs text-rose-600 font-semibold" data-testid="cart-insufficient">
                            Insufficient wallet balance. Top up or apply for inventory credit.
                        </p>
                    )}
                    <button
                        onClick={openCheckout}
                        disabled={cartItems.length === 0}
                        className="og-btn-gradient w-full mt-4 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        data-testid="checkout-btn"
                    >
                        <ShoppingCart size={16} /> Review &amp; checkout
                    </button>
                </aside>
            </div>

            {/* Recent orders */}
            <div className="og-card p-6">
                <h3 className="font-display text-2xl font-bold text-onego-navy">Recent orders</h3>
                <div className="mt-3 divide-y divide-slate-100">
                    {orders.length === 0 && <p className="py-4 text-sm text-slate-500">You haven&rsquo;t placed any orders yet.</p>}
                    {orders.slice(0, 8).map((o) => (
                        <div key={o.id} className="py-3 flex items-center justify-between" data-testid={`order-row-${o.id}`}>
                            <div>
                                <p className="font-bold text-onego-navy">{o.items.length} items</p>
                                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()} {o.delivery_address ? `\u00b7 ${o.delivery_address}` : ""}</p>
                            </div>
                            <p className="font-display font-bold text-onego-navy">KES {fmt(o.total)}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Checkout dialog */}
            <Dialog open={checkoutOpen} onOpenChange={(o) => { setCheckoutOpen(o); if (!o) setStep("review"); }}>
                <DialogContent className="max-w-2xl" data-testid="checkout-dialog">
                    {step === "review" && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">Review your order</DialogTitle>
                                <DialogDescription>Confirm items and delivery details. Funds will be debited from your wallet.</DialogDescription>
                            </DialogHeader>
                            <div className="max-h-[320px] overflow-auto divide-y divide-slate-100" data-testid="checkout-items">
                                {cartItems.map((it) => (
                                    <div key={it.id} className="py-3 flex items-center justify-between gap-4 text-sm">
                                        <div>
                                            <p className="font-bold text-onego-navy">{it.name}</p>
                                            <p className="text-xs text-slate-500">{it.brand || it.supplier} &middot; {it.qty} &times; KES {fmt(it.price)}</p>
                                        </div>
                                        <p className="font-bold text-onego-navy">KES {fmt(it.qty * it.price)}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2">
                                <p className="text-sm font-semibold text-onego-navy mb-2">Fulfilment</p>
                                <div className="grid grid-cols-2 gap-2" data-testid="fulfilment-toggle">
                                    <button
                                        type="button"
                                        onClick={() => setFulfilmentMode("delivery")}
                                        className={`p-3 rounded-2xl border text-left transition ${fulfilmentMode === "delivery" ? "border-pink-500 bg-pink-50/40" : "border-slate-200 hover:border-onego-navy"}`}
                                        data-testid="fulfilment-delivery"
                                    >
                                        <p className="font-bold text-onego-navy text-sm">Deliver to my kiosk</p>
                                        <p className="text-xs text-slate-500">Last-mile to your address</p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFulfilmentMode("pickup")}
                                        disabled={pickupHubs.length === 0}
                                        className={`p-3 rounded-2xl border text-left transition disabled:opacity-50 ${fulfilmentMode === "pickup" ? "border-pink-500 bg-pink-50/40" : "border-slate-200 hover:border-onego-navy"}`}
                                        data-testid="fulfilment-pickup"
                                    >
                                        <p className="font-bold text-onego-navy text-sm">Collect from a hub</p>
                                        <p className="text-xs text-slate-500">
                                            {pickupHubs[0] ? `Nearest: ${pickupHubs[0].name} • ${pickupHubs[0].distance_km} km` : "No hubs in range"}
                                        </p>
                                    </button>
                                </div>
                            </div>

                            {fulfilmentMode === "pickup" && pickupHubs.length > 0 && (
                                <div className="space-y-2" data-testid="pickup-hub-options">
                                    {pickupHubs.map((h) => (
                                        <button
                                            type="button"
                                            key={h.id}
                                            onClick={() => setSelectedHubId(h.id)}
                                            className={`w-full p-3 rounded-2xl border text-left transition flex items-center justify-between ${selectedHubId === h.id ? "border-pink-500 bg-pink-50/40" : "border-slate-200 hover:border-onego-navy"}`}
                                            data-testid={`pickup-hub-${h.id}`}
                                        >
                                            <div className="min-w-0">
                                                <p className="font-bold text-onego-navy text-sm truncate">{h.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    {h.city || h.country} &middot; {h.hours} &middot; cap {h.capacity}/day
                                                </p>
                                            </div>
                                            <div className="text-right pl-3 shrink-0">
                                                <p className="font-display font-extrabold text-onego-navy">{h.distance_km} km away</p>
                                                {h.rating > 0 && (
                                                    <p className="text-xs text-slate-500">★ {h.rating} ({h.review_count})</p>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {fulfilmentMode === "delivery" && (
                                <div>
                                    <label className="text-sm font-semibold text-onego-navy">Delivery address (optional)</label>
                                    <input className="og-input mt-1" value={delivery} onChange={(e) => setDelivery(e.target.value)} placeholder="Stall name, street, town" data-testid="checkout-delivery-input" />
                                </div>
                            )}
                            <div className="og-divider-gradient my-3" />
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-slate-500">Wallet after order</p>
                                <p className={`font-bold ${insufficient ? "text-rose-600" : "text-onego-navy"}`}>KES {fmt(walletBalance - total)}</p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-slate-500">Total to pay</p>
                                <p className="font-display text-2xl font-extrabold og-gradient-text">KES {fmt(total)}</p>
                            </div>
                            <DialogFooter>
                                <button onClick={() => setCheckoutOpen(false)} className="og-btn-ghost" data-testid="checkout-cancel-btn">Cancel</button>
                                <button onClick={checkout} disabled={loading || insufficient} className="og-btn-gradient disabled:opacity-60" data-testid="checkout-confirm-btn">
                                    {loading ? "Placing\u2026" : insufficient ? "Insufficient balance" : `Pay KES ${fmt(total)}`}
                                </button>
                            </DialogFooter>
                        </>
                    )}
                    {step === "confirmed" && confirmation && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl flex items-center gap-2"><Check className="text-emerald-500" /> Order confirmed</DialogTitle>
                                <DialogDescription>Your order has been placed and is being processed by suppliers.</DialogDescription>
                            </DialogHeader>
                            <div className="og-card p-5 bg-onego-bg">
                                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Order reference</p>
                                <p className="font-display text-xl font-extrabold text-onego-navy">{confirmation.id.slice(0, 8).toUpperCase()}</p>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-slate-500">Items</p>
                                        <p className="font-bold text-onego-navy">{confirmation.items.length}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Total</p>
                                        <p className="font-bold text-onego-navy">KES {fmt(confirmation.total)}</p>
                                    </div>
                                    {confirmation.delivery_address && (
                                        <div className="col-span-2 flex items-center gap-2">
                                            <MapPin size={14} className="text-slate-400" />
                                            <p className="text-slate-700">{confirmation.delivery_address}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <DialogFooter>
                                <button onClick={() => setCheckoutOpen(false)} className="og-btn-ghost" data-testid="checkout-done-btn">Done</button>
                                <a href="/dashboard/logistics" className="og-btn-gradient" data-testid="checkout-track-btn">Track delivery</a>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
